package org.example.project.enginekalender

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import kotlinx.browser.window
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.PopupEngineCard
import org.example.project.service.GlobalManager
import org.example.project.settings.LumaticaButton
import org.example.project.screens_shared.taskCompactPreview
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.TouchEvent

private fun taskTime(minutes: Int): String = "${(minutes / 60).toString().padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}"

private fun newTask(date: String, startMinute: Int): All = All(
    datum = date, uhrzeitVon = taskTime(startMinute),
    uhrzeitBis = taskTime(startMinute + 15),
    aufgabeTyp = "TEXT"
)

@Composable
fun KalenderAufgabenCalendar(
    selectedDate: String,
    onSelectDate: (String) -> Unit,
    art: String,
    kalenderUserId: String,
    kalenderFirmenId: String = "",
    ansicht: String,
    monthDayOpen: Boolean,
    onMonthDayOpen: (Boolean) -> Unit,
    copyKind: String,
    copySource: String,
    copyRequestToken: Int,
    onCopyComplete: () -> Unit,
    editMode: Boolean = false,
    compact: Boolean = false
) {
    val scope = rememberCoroutineScope()
    val liveVersion = GlobalManager.jsonDomainVersion("aufgaben")
    var tasks by remember { mutableStateOf<List<All>>(emptyList()) }
    var editor by remember { mutableStateOf<All?>(null) }
    var error by remember { mutableStateOf("") }
    var saving by remember { mutableStateOf(false) }
    var copyTarget by remember { mutableStateOf("") }
    var copying by remember { mutableStateOf(false) }
    var weekSwipeStart by remember(selectedDate, ansicht) { mutableStateOf<Pair<Double, Double>?>(null) }
    var slotSettings by remember(art, kalenderUserId, kalenderFirmenId) {
        mutableStateOf(Triple("08:00", "18:00", setOf("mo", "di", "mi", "do", "fr", "sa", "so")))
    }
    val phone = compact && window.innerWidth <= 760
    val copySourceDate = parseKalenderIsoDate(copySource)
    val markedSourceDates = if (copyKind == "Woche" && copySourceDate != null) {
        val start = kalenderStartOfWeek(copySourceDate)
        (0..6).map { kalenderAddDays(start.year, start.month, start.day, it) }.toSet()
    } else if (copyKind == "Tag") setOf(copySource) else emptySet()

    LaunchedEffect(liveVersion) {
        tasks = runCatching { loadOwnKalenderAufgaben() }.getOrElse {
            error = "Aufgaben konnten nicht geladen werden."
            emptyList()
        }
    }
    LaunchedEffect(art, kalenderUserId, kalenderFirmenId, GlobalManager.jsonDomainVersion("kalender")) {
        slotSettings = if (kalenderFirmenId.isNotBlank()) {
            loadScopedKalenderSlotSettings(kalenderFirmenId, kalenderUserId)
        } else {
            loadKalenderSlotSettings(art, kalenderUserId)
        }
    }
    LaunchedEffect(copyKind, copySource) {
        if (copyKind.isNotBlank()) copyTarget = copySource
    }
    LaunchedEffect(copyRequestToken) {
        if (copyRequestToken > 0 && copyKind.isNotBlank() && editMode) copying = true
    }
    LaunchedEffect(editMode) {
        editor = null
        if (!editMode) copying = false
    }

    fun updateTask(task: All) {
        if (saving) return
        scope.launch {
            saving = true
            error = ""
            try {
                saveOwnKalenderAufgabe(task)
                tasks = loadOwnKalenderAufgaben()
                editor = null
            } catch (cause: Throwable) {
                error = cause.message ?: "Aufgabe konnte nicht gespeichert werden."
            } finally { saving = false }
        }
    }

    Div({ style {
        width(100.percent); height(100.percent); minHeight(0.px)
        display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px)
    } }) {
        if (error.isNotBlank() && editor == null && !copying) {
            Span({ style { color(Color("#ff9b9b")); fontSize(13.px) } }) { Text(error) }
        }
        Div({ style {
            flexGrow(1); minHeight(0.px); minWidth(0.px)
            display(DisplayStyle.Grid)
            property("grid-template-columns", when {
                ansicht == "Monat" && monthDayOpen && compact && !phone -> "876px minmax(0,1fr)"
                ansicht == "Monat" && monthDayOpen && !phone -> "max-content minmax(0,1fr)"
                else -> "minmax(0,1fr)"
            })
            gap(8.px); overflow("hidden")
        } }) {
            if (ansicht == "Monat" && !(phone && monthDayOpen)) {
                Div({ style {
                    property("width", if (compact) "100%" else if (monthDayOpen) "auto" else "100%")
                    height(100.percent); minHeight(0.px); minWidth(0.px)
                    display(DisplayStyle.Flex)
                    justifyContent(if (monthDayOpen) JustifyContent.FlexStart else JustifyContent.Center)
                    alignItems(if (compact && !phone) AlignItems.FlexStart else AlignItems.Center)
                    padding(if (phone) 0.px else 8.px); boxSizing("border-box")
                } }) {
                    Div({ style { property("width", if (compact) "100%" else "auto"); height(100.percent); minHeight(0.px)
                        if (!compact) property("aspect-ratio", "1 / 1")
                        property("max-width", "100%");
                        display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); boxSizing("border-box") } }) {
                        Monatsübersicht(
                            selectedDate = selectedDate, art = "AUFGABEN", kalenderUserId = "",
                            bookingsOverride = tasks, showBookingHover = false,
                            dayTextByDate = tasks.groupBy { it.datum }.mapValues { it.value.size.toString() },
                            dayTextColorByDate = tasks.groupBy { it.datum }.mapValues { "#59A7FF" },
                            highlightedDates = markedSourceDates,
                            onSelectDate = { onSelectDate(it); onMonthDayOpen(true) }
                        )
                    }
                }
            }
            if (ansicht == "Woche" || ansicht == "Tag" || monthDayOpen) {
                Div({
                    style { minHeight(0.px); minWidth(0.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                    if (compact && !phone) padding(8.px, if (ansicht == "Monat" && monthDayOpen) 18.px else 8.px)
                    boxSizing("border-box") }
                    onTouchStart {
                        if (ansicht != "Woche" || phone) return@onTouchStart
                        val touch = it.touches.item(0) ?: return@onTouchStart
                        weekSwipeStart = touch.clientX.toDouble() to touch.clientY.toDouble()
                    }
                    onTouchEnd {
                        if (ansicht != "Woche" || phone) return@onTouchEnd
                        val start = weekSwipeStart ?: return@onTouchEnd
                        weekSwipeStart = null
                        val touchEvent = it.nativeEvent as? TouchEvent ?: return@onTouchEnd
                        val touch = touchEvent.changedTouches.item(0) ?: return@onTouchEnd
                        val direction = kalenderWeekSwipeDirection(
                            start.first, start.second, touch.clientX.toDouble(), touch.clientY.toDouble()
                        ) ?: return@onTouchEnd
                        parseKalenderIsoDate(selectedDate)?.let { date ->
                            onSelectDate(kalenderAddDays(date.year, date.month, date.day, direction * 7))
                        }
                    }
                }) {
                    AufgabenTimeGrid(
                        selectedDate = selectedDate,
                        week = ansicht == "Woche" && !phone,
                        tasks = tasks,
                        visibleHours = visibleKalenderHours(buildKalenderWeekDetailData(selectedDate), slotSettings.first, slotSettings.second),
                        allowedFrom = slotSettings.first,
                        allowedTo = slotSettings.second,
                        activeWeekdays = slotSettings.third,
                        markedKind = if (editMode) copyKind else "",
                        markedDate = copySource,
                        editMode = editMode,
                        onAdd = { date, minute -> if (editMode) editor = newTask(date, minute) },
                        onEdit = { task -> if (editMode) editor = task }
                    )
                }
            }
        }
    }

    editor?.let { task ->
        TerminBookingPopup(
            popupState = task,
            art = art,
            allowedFrom = slotSettings.first,
            allowedTo = slotSettings.second,
            onClose = { if (!saving) { editor = null; error = "" } },
            onSave = { updateTask(it) },
            taskMode = true,
            saving = saving,
            saveError = error,
            onDeleteTask = if (task.id.isBlank()) null else {{
                if (!saving) scope.launch {
                    saving = true
                    error = ""
                    try {
                        deleteOwnKalenderAufgabe(task)
                        tasks = loadOwnKalenderAufgaben()
                        editor = null
                    } catch (cause: Throwable) {
                        error = cause.message ?: "Aufgabe konnte nicht gelöscht werden."
                    } finally { saving = false }
                }
            }}
        )
    }

    if (copying) {
        PopupEngineCard(windowKey = "aufgaben-kopieren", widthPx = if (phone) window.innerWidth - 16 else 480, heightPx = 510,
            minWidthPx = if (phone) 280 else 420, minHeightPx = 440, viewportAnchored = true,
            initialX = if (phone) 8.0 else ((window.innerWidth - 480) / 2.0), initialY = 40.0,
            onClose = { copying = false }) {
            Div({ style { padding(24.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(14.px) } }) {
                Span({ style { color(Color.white); fontSize(21.px); fontWeight("700") } }) {
                    Text("$copyKind übernehmen auf")
                }
                Span({ style { color(DashboardTheme.TEXT_MUTED); fontSize(13.px) } }) {
                    Text(if (copyKind == "Tag") "Zieltag im Kalender wählen" else "Tag in der Zielwoche wählen")
                }
                AufgabenCopyCalendar(selectedDate = copyTarget.ifBlank { selectedDate },
                    markWeek = copyKind == "Woche", onSelectDate = { copyTarget = it })
                LumaticaButton(label = if (copyKind == "Tag") "Tag kopieren" else "Woche kopieren",
                    onClick = {
                        val source = parseKalenderIsoDate(copySource) ?: return@LumaticaButton
                        val target = parseKalenderIsoDate(copyTarget) ?: return@LumaticaButton
                        val sourceStart = if (copyKind == "Woche") kalenderStartOfWeek(source) else source
                        val targetStart = if (copyKind == "Woche") kalenderStartOfWeek(target) else target
                        val count = if (copyKind == "Woche") 7 else 1
                        scope.launch {
                            try {
                                copyOwnKalenderAufgaben(
                                    (0 until count).map { kalenderAddDays(sourceStart.year, sourceStart.month, sourceStart.day, it) },
                                    (0 until count).map { kalenderAddDays(targetStart.year, targetStart.month, targetStart.day, it) }
                                )
                                tasks = loadOwnKalenderAufgaben()
                                copying = false
                                onCopyComplete()
                                error = ""
                            } catch (cause: Throwable) {
                                error = cause.message ?: "Kopieren fehlgeschlagen."
                            }
                        }
                    }, heightPx = 36)
                if (error.isNotBlank()) Span({ style { color(Color("#ff9b9b")) } }) { Text(error) }
            }
        }
    }
}

@Composable
private fun AufgabenTimeGrid(
    selectedDate: String,
    week: Boolean,
    tasks: List<All>,
    visibleHours: List<Int>,
    allowedFrom: String,
    allowedTo: String,
    activeWeekdays: Set<String>,
    markedKind: String,
    markedDate: String,
    editMode: Boolean,
    onAdd: (String, Int) -> Unit,
    onEdit: (All) -> Unit
) {
    val detail = remember(selectedDate) { buildKalenderWeekDetailData(selectedDate) }
    val days = if (week) detail.module else detail.module.filter { it.datum == selectedDate }
    val markedWeek = parseKalenderIsoDate(markedDate)?.let { kalenderStartOfWeek(it) }
    val markedDates = if (markedKind == "Woche" && markedWeek != null) {
        (0..6).map { kalenderAddDays(markedWeek.year, markedWeek.month, markedWeek.day, it) }.toSet()
    } else if (markedKind == "Tag") setOf(markedDate) else emptySet()
    fun tasksInSlot(date: String, minute: Int): List<All> = tasks
        .filter { it.datum == date && (parseHourMinute(it.uhrzeitVon) ?: -1) / 15 * 15 == minute }
        .sortedWith(compareBy<All> { it.uhrzeitVon }.thenBy { it.bezeichnung })
    fun taskCoveringSlot(date: String, minute: Int): All? = tasks.firstOrNull { task ->
        val start = parseHourMinute(task.uhrzeitVon) ?: return@firstOrNull false
        val end = parseHourMinute(task.uhrzeitBis) ?: return@firstOrNull false
        task.datum == date && start < minute + 15 && end > minute
    }
    Style(KalenderWeekOverviewStyles)
    Div({ style {
        height(100.percent); minWidth(0.px); minHeight(0.px); overflow("auto")
        property("background", "#141820"); property("border", "1px solid rgba(255,255,255,.14)")
        borderRadius(8.px)
        boxSizing("border-box")
    } }) {
        KalenderNativeWeekGrid(
            detailData = detail.copy(module = days),
            visibleHours = visibleHours,
            allowedFrom = allowedFrom,
            allowedTo = allowedTo,
            bookings = tasks,
            cellBackgroundColors = emptyMap(),
            activeWeekdays = activeWeekdays,
            fillVisibleHours = false,
            selectedBookingCell = null,
            onSelectedBookingCellChange = {},
            onEmptyCellClick = { rowId, colIndex ->
                if (editMode) days.getOrNull(colIndex)?.let { day ->
                    val minute = parseHourMinute(rowId) ?: 8 * 60
                    val existingTask = taskCoveringSlot(day.datum, minute)
                    if (existingTask != null) onEdit(existingTask) else onAdd(day.datum, minute)
                }
            },
            onBookingClick = { _, _, _ -> },
            onBookingHover = { _, _, _ -> },
            onBookingHoverLeave = {},
            showPrivateBookingDetails = true,
            taskMode = true,
            markedTaskDates = markedDates,
            taskCellContent = { date, minute ->
                val cellTasks = tasksInSlot(date, minute)
                Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(4.px); minWidth(0.px) } }) {
                    cellTasks.forEachIndexed { taskIndex, task ->
                            val width = 100.0 / cellTasks.size.coerceAtLeast(1)
                            val preview = task.aufgabeZeilen.joinToString(" | ") {
                                it.label + if (it.erledigt) " ✓" else ""
                            }
                                .ifBlank { taskCompactPreview(task.beschreibung.orEmpty()) }
                                .ifBlank { task.bezeichnung.ifBlank { "Aufgabe" } }
                            Div({
                                attr("title", preview)
                                style {
                                    position(Position.Absolute); top(0.px); left((taskIndex * width).percent)
                                    property("width", "$width%")
                                    height(32.px); property("z-index", "3")
                                    overflow("hidden"); boxSizing("border-box")
                                    color(Color.white); fontSize(13.px); lineHeight("1.35"); padding(6.px, 8.px)
                                    property("white-space", "nowrap")
                                    property("text-overflow", "ellipsis")
                                }
                                onClick { event ->
                                    event.stopPropagation()
                                    onEdit(task)
                                }
                            }) { Text(preview) }
                    }
                    }
            }
        )
    }
}

@Composable
private fun AufgabenCopyCalendar(selectedDate: String, markWeek: Boolean, onSelectDate: (String) -> Unit) {
    var monthCursor by remember { mutableStateOf(selectedDate) }
    val month = remember(monthCursor, selectedDate) { buildKalenderMonthData(selectedDate, monthCursor) }
    val selected = parseKalenderIsoDate(selectedDate)
    val weekStart = selected?.let { kalenderStartOfWeek(it) }
    val markedDates = if (markWeek && weekStart != null) {
        (0..6).map { kalenderAddDays(weekStart.year, weekStart.month, weekStart.day, it) }.toSet()
    } else setOf(selectedDate)
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px) } }) {
        Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.SpaceBetween) } }) {
            LumaticaButton(label = "‹", onClick = { monthCursor = month.previousMonthIso }, heightPx = 30)
            Span({ style { color(Color.white); fontWeight("700") } }) { Text(month.bezeichnung) }
            LumaticaButton(label = "›", onClick = { monthCursor = month.nextMonthIso }, heightPx = 30)
        }
        Div({ style { display(DisplayStyle.Grid); gridTemplateColumns("repeat(7, minmax(0, 1fr))"); gap(3.px) } }) {
            (0..6).forEach { day ->
                Span({ style { color(DashboardTheme.TEXT_MUTED); textAlign("center"); fontSize(11.px) } }) {
                    Text(kalenderWeekdayShortName(day))
                }
            }
            month.module.forEach { day ->
                Div({ style {
                    height(34.px); display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center); borderRadius(6.px); cursor("pointer")
                    color(if (day.aktiv) Color.white else DashboardTheme.TEXT_MUTED)
                    property("background", if (day.datum in markedDates) "rgba(87,145,225,.60)" else "rgba(255,255,255,.06)")
                }
                    onClick { onSelectDate(day.datum); monthCursor = day.datum }
                }) { Text(day.label) }
            }
        }
    }
}
